import React from "react";
import { Box, Paper, Typography, IconButton, Button } from "@mui/material";
import { X, Check } from "@phosphor-icons/react";
import LoadingButton from "@mui/lab/LoadingButton";
import { format } from "date-fns";
import { convertTokens } from "@date-fns/upgrade/v2";
import { theme } from "@answersnow/pkg-theme";

export interface PhaseTrackerStep {
  id?: string;
  title: string;
  subtitle?: string;
  description?: string;
  completed: boolean;
  isCurrent?: boolean;
  completedDate?: string | Date;
  button?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
    icon?: React.ReactNode;
  };
}

export interface PhaseTrackerProps {
  title?: string;
  steps: PhaseTrackerStep[];
  onClose?: () => void;
}

function StepIcon({
  state,
  stepNumber,
}: Readonly<{
  state: "done" | "current" | "pending";
  stepNumber: number;
}>) {
  if (state === "done") {
    return (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "100px",
          backgroundColor: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Check size={14} weight="bold" color={theme.palette.primary.contrastText} />
      </Box>
    );
  }

  if (state === "current") {
    return (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "100px",
          backgroundColor: "primary.main",
          color: "primary.contrastText",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        {stepNumber}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "100px",
        backgroundColor: "text.disabled",
        color: "primary.contrastText",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 400,
        flexShrink: 0,
      }}
    >
      {stepNumber}
    </Box>
  );
}

function ConnectorLine() {
  return (
    <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-start" }}>
      <Box sx={{ width: 24, display: "flex", justifyContent: "center", flexShrink: 0, px: "11px" }}>
        <Box sx={{ width: 2, height: 36, backgroundColor: "grey.400" }} />
      </Box>
    </Box>
  );
}


export const PhaseTracker: React.FC<PhaseTrackerProps> = ({
  title = "Onboarding Status",
  steps,
  onClose,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: theme.radius["ex-large"],
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 7,
          py: 5,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: 24,
            fontWeight: 500,
            letterSpacing: 0.2,
          }}
        >
          {title}
        </Typography>
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ padding: "5px", borderRadius: theme.radius.button.sm }}
            aria-label="Close"
          >
            <X size={20} />
          </IconButton>
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", backgroundColor: "background.paper", p: "24px" }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {steps.map((step, index) => {
            const stepNumber = index + 1;
            type StepState = "done" | "current" | "pending";
            let state: StepState = "pending";
            if (step.completed) state = "done";
            else if (step.isCurrent) state = "current";
            const displaySubtitle =
              step.subtitle ??
              (step.completed && step.completedDate
                ? `Completed ${format(new Date(step.completedDate), convertTokens("MMM D, YYYY @ hh:mm a"))}`
                : undefined);

            return (
              <React.Fragment key={step.id ?? index}>
                <Box
                  sx={{ display: "flex", gap: 1, alignItems: "flex-start", width: "100%", my: 4 }}
                >
                  <StepIcon state={state} stepNumber={stepNumber} />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: step.isCurrent && step.description ? 2 : 0,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={step.completed || step.isCurrent ? 500 : 400}
                        color="text.primary"
                      >
                        {step.title}
                      </Typography>
                      {(displaySubtitle || step.description) && (
                        <Typography variant="body.caption" color="text.secondary" display="block" mt={1}>
                          {displaySubtitle ?? step.description}
                        </Typography>
                      )}
                    </Box>
                    {step.button && (
                      <Box sx={{ mt: 1 }}>
                        {step.button.loading === undefined ? (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={step.button.onClick}
                            startIcon={step.button.icon}
                            sx={{
                              px: 4,
                              py: 1,
                              boxShadow: "0px 1px 1px rgba(0,0,0,0.15)",
                              fontWeight: 500,
                              textTransform: "capitalize",
                              borderRadius: theme.radius.button.sm,
                            }}
                          >
                            {step.button.label}
                          </Button>
                        ) : (
                          <LoadingButton
                            variant="contained"
                            color="primary"
                            size="small"
                            loading={step.button.loading}
                            onClick={step.button.onClick}
                            startIcon={step.button.icon}
                            sx={{
                              px: 4,
                              py: 1,
                              boxShadow: "0px 1px 1px rgba(0,0,0,0.15)",
                              fontWeight: 500,
                              textTransform: "capitalize",
                              borderRadius: theme.radius.button.sm,
                            }}
                          >
                            {step.button.label}
                          </LoadingButton>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
                {index < steps.length - 1 && <ConnectorLine />}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

export default PhaseTracker;
